import React from 'react';
import dropRight from 'lodash/dropRight';
import isEmpty from 'lodash/isEmpty';
import last from 'lodash/last';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';

const { Provider, Consumer } = React.createContext({});

export const steps = {
  intro: 'intro',
  selectSolutions: 'selectSolutions',
  selectWorkspaceOptions: 'selectWorkspaceOptions',
  geoNarrowing: 'geoNarrowing',
};

const stepNumber = {
  [steps.intro]: 0,
  [steps.selectSolutions]: 1,
  [steps.selectWorkspaceOptions]: 2,
  [steps.geoNarrowing]: 3,
};

const BASE_LANGUAGE = 'en-us';

class SolutionBuilderProvider extends React.Component {
  submitFormTrigger = () => {};
  state = {
    stepData: {
      [steps.selectSolutions]: {},
      [steps.selectWorkspaceOptions]: {},
      [steps.geoNarrowing]: {},
    },
    stepNumber: stepNumber[steps.intro],
    currentStep: steps.intro,
    breadcrumbs: [],
    isProgressBarVisible: false,
    isSidePanelOpen: false,
    isHelpMessageVisible: false,
    isNextEnabled: false,
    helpText: '',
    omitGeoNarrowing: false,
  };

  setSubmitFormTrigger = fn => {
    this.submitFormTrigger = fn;
  };

  submitCurrentForm = () => {
    if (isFunction(this.submitFormTrigger)) this.submitFormTrigger();
  };

  goPrev = () => {
    const { breadcrumbs, currentStep } = this.state;
    const previousStep = last(breadcrumbs);
    const breadcrumbRest = dropRight(breadcrumbs);

    this.submitFormTrigger = () => {};

    const newStepData = {
      ...this.state.stepData,
      [currentStep]: {},
    };

    this.setState({
      currentStep: previousStep,
      stepNumber: stepNumber[previousStep],
      breadcrumbs: breadcrumbRest,
      isProgressBarVisible: previousStep !== steps.intro,
      isHelpMessageVisible: false,
      stepData: newStepData,
    });
  };

  goNext = () => {
    const { currentStep, breadcrumbs } = this.state;
    const nextState = this.getNextStepState();

    this.submitFormTrigger = () => {};

    if (nextState) {
      const newStepNumber = stepNumber[nextState.currentStep];
      this.setState({
        isNextEnabled: false,
        isHelpMessageVisible: false,
        breadcrumbs: [...breadcrumbs, currentStep],
        stepNumber: newStepNumber,
        ...nextState,
      });
    }
  };

  showHelpMessage = () => {
    if (!this.state.isHelpMessageVisible) {
      this.setState({ isHelpMessageVisible: true });
    }
  };

  getNextStepState = () => {
    const { currentStep, omitGeoNarrowing } = this.state;

    if (currentStep === steps.intro) {
      return {
        currentStep: steps.selectSolutions,
        isProgressBarVisible: true,
      };
    }

    if (currentStep === steps.selectSolutions) {
      const selectSolutionData = this.state.stepData[steps.selectSolutions];
      const nextStep = selectSolutionData.officeSpace
        ? steps.selectWorkspaceOptions
        : steps.geoNarrowing;

      if (nextStep === steps.geoNarrowing && omitGeoNarrowing) {
        this.generateUrl();
        return null;
      }

      return {
        currentStep: nextStep,
      };
    }

    if (currentStep === steps.selectWorkspaceOptions) {
      if (omitGeoNarrowing) {
        this.generateUrl();
        return null;
      }

      return {
        geoLocationContext: '',
        currentStep: steps.geoNarrowing,
      };
    }

    if (currentStep === steps.geoNarrowing) {
      this.generateUrl();
      return null;
    }
  };

  generateUrl() {
    const selectSolutions = get(this.state, 'stepData.selectSolutions', {});
    const selectWorkspaceOptions = get(
      this.state,
      'stepData.selectWorkspaceOptions',
      {},
    );
    const selectedNodes = get(
      this.state,
      'stepData.geoNarrowing.selectedNodes',
      [],
    );
    const geoLocationContext = get(
      this.state,
      'stepData.geoNarrowing.geoLocationContext',
      '',
    );
    const language = get(
      this.state,
      'stepData.geoNarrowing.language',
      BASE_LANGUAGE,
    );
    let selectedSolutionsParams = '';
    let officeSpaceParams = '';

    selectedSolutionsParams += selectSolutions.officeSpace ? '&ws=true' : '';
    selectedSolutionsParams += selectSolutions.businessAddress
      ? '&ba=true'
      : '';
    selectedSolutionsParams += selectSolutions.businessTelephoneNumber
      ? '&ta=true'
      : '';

    if (selectSolutions && selectSolutions.officeSpace) {
      if (selectWorkspaceOptions.coworking) {
        officeSpaceParams += selectWorkspaceOptions.coworkingNumber1
          ? `&dd=true&ddp=${selectWorkspaceOptions.coworkingNumber1}`
          : '';
        officeSpaceParams += selectWorkspaceOptions.coworkingNumber2
          ? `&cm=true&cmp=${selectWorkspaceOptions.coworkingNumber2}`
          : '';
      }
      if (selectWorkspaceOptions.office) {
        officeSpaceParams += selectWorkspaceOptions.officeNumber1
          ? `&do=true&dop=${selectWorkspaceOptions.officeNumber1}`
          : '';
        officeSpaceParams += selectWorkspaceOptions.officeNumber2
          ? `&om=true&omp=${selectWorkspaceOptions.officeNumber2}`
          : '';
      }
    }

    const geoLocations = isEmpty(selectedNodes)
      ? geoLocationContext
      : selectedNodes.join('|');

    const resultsUrl = `/${language}/solution-builder/results?geoIds=${geoLocations}${selectedSolutionsParams}${officeSpaceParams}`;

    window.location.href = resultsUrl;
  }

  setStepData = newData => {
    const { stepData, currentStep } = this.state;
    this.setState(
      {
        stepData: {
          ...stepData,
          [currentStep]: newData,
        },
      },
      this.goNext,
    );
  };

  getStepData = () => {
    const { stepData, currentStep } = this.state;
    return stepData[currentStep] || {};
  };

  getCurrentStepFields = (fields = {}) => {
    const { currentStep } = this.state;
    const stepFields = {
      [steps.intro]: ['IntroBodyText', 'IntroHeadline', 'IntroStartCTA'],
      [steps.selectSolutions]: [
        'ProductHeadline',
        'ProductSubtitle',
        'ProductSidePanelCTA',
        'OfficeSpaceCopy',
        'BusinessAddressCopy',
        'BusinessTelephoneCopy',
        'ProductSelectMoreText',
        'ProductsSidePanelHeadline',
        'ProductsSidePanelBusinessAddress',
        'ProductsSidePanelBusinessTelephone',
        'ProductsSidePanelOfficeSpace',
      ],
      [steps.selectWorkspaceOptions]: [
        'NumbersHeadline',
        'NumbersSubtitle',
        'NumbersSidePanelCTA',
        'NumbersOfficeText',
        'NumbersDedicatedOfficeText',
        'NumbersOfficeMembershipText',
        'NumbersCoworkingText',
        'NumbersDedicatedDeskText',
        'NumbersCoworkMembershipText',
        'NumbersMaxPeopleNumber',
        'NumbersFootNote',
        'NumbersSidePanelHeadline',
        'NumbersSidePanelDedicatedOffice',
        'NumbersSidePanelOfficeMembership',
        'NumbersSidePanelDedicatedDesk',
        'NumbersSidePanelCoworkMembership',
      ],
      [steps.geoNarrowing]: [
        'GeoNarrowingHeadlineContext',
        'GeoNarrowingHeadlineNoContext',
        'GeoNarrowingSubtitle',
        'GeoNarrowingFootNote',
      ],
    };

    let currentStepFields = {};

    if (currentStep && stepFields[currentStep]) {
      currentStepFields = stepFields[currentStep].reduce((acc, cur) => {
        acc[cur] = fields[cur];
        return acc;
      }, {});
    }

    return currentStepFields;
  };

  toggleSidePanel = () => {
    this.setState({ isSidePanelOpen: !this.state.isSidePanelOpen });
  };

  enableNext = () => {
    if (this.state.isNextEnabled) return;
    this.setState({ isNextEnabled: true });
  };

  disableNext = () => {
    if (!this.state.isNextEnabled) return;
    this.setState({ isNextEnabled: false });
  };

  setHelpText = (helpText = '') => {
    this.setState({ helpText });
  };

  setStateForStep = (step, state) => {
    this.setState({
      stepData: {
        ...this.state.stepData,
        [step]: {
          ...this.state.stepData[step],
          ...state,
        },
      },
    });
  };

  omitGeoNarrowing = () => {
    this.setState({ omitGeoNarrowing: true });
  };

  render() {
    return (
      <Provider
        value={{
          currentStep: this.state.currentStep,
          isNextEnabled: this.state.isNextEnabled,
          stepNumber: this.state.stepNumber,
          helpText: this.state.helpText,
          setSubmitFormTrigger: this.setSubmitFormTrigger,
          setStepData: this.setStepData,
          getStepData: this.getStepData,
          submitCurrentForm: this.submitCurrentForm,
          goPrev: this.goPrev,
          goNext: this.goNext,
          steps: steps,
          isProgressBarVisible: this.state.isProgressBarVisible,
          getCurrentStepFields: this.getCurrentStepFields,
          toggleSidePanel: this.toggleSidePanel,
          isSidePanelOpen: this.state.isSidePanelOpen,
          isHelpMessageVisible: this.state.isHelpMessageVisible,
          showHelpMessage: this.showHelpMessage,
          enableNext: this.enableNext,
          disableNext: this.disableNext,
          setHelpText: this.setHelpText,
          setStateForStep: this.setStateForStep,
          omitGeoNarrowing: this.omitGeoNarrowing,
        }}
      >
        {this.props.children}
      </Provider>
    );
  }
}

export { SolutionBuilderProvider, Consumer as SolutionBuilderConsumer };
